import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Container,
  Paper,
  Grid,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SendHorizontal, CheckCircle } from 'lucide-mui';
import { RootState } from '../../reducers';
import { sendEmail } from '../../services/emailService';
import { wrapWithTheme } from '../email/templates';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  boxShadow: 'none',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 24,
    backgroundColor: theme.palette.background.default,
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 24,
  '& .MuiButton-endIcon': {
    marginLeft: theme.spacing(1),
  },
}));

const SuccessMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  '& .icon': {
    color: theme.palette.success.main,
    width: 64,
    height: 64,
    marginBottom: theme.spacing(2),
  },
  '& .title': {
    color: theme.palette.success.main,
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  '& .subtitle': {
    color: theme.palette.text.secondary,
  },
}));

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface ContactSupportFormProps {
  formSource?: string;
}

const ContactSupportForm: React.FC<ContactSupportFormProps> = ({
  formSource,
}) => {
  const { user } = useSelector((state: RootState) => state.settings);
  const { subscription, usage, marketing } = useSelector(
    (state: RootState) => state.plan
  );
  const { selectedTeam } = useSelector((state: RootState) => state.team);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState<FormData>({
    name: user?.name || '',
    email: user?.email || '',
    message: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validateForm = () => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const emailContent = `
          <h2>Support Request from User</h2>
          
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Form Source:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formSource || 'Contact Support Form'
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Name:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.name
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Email:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.email
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Message:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${formData.message.replace(
                /\n/g,
                '<br />'
              )}</td>
            </tr>
            ${
              marketing?.intent
                ? `
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Initial Intent:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${marketing.intent}</td>
            </tr>
            `
                : ''
            }

            <tr>
              <td colspan="2" style="padding: 24px 0 12px 0;"><strong>User Information</strong></td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Current Plan:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                subscription?.package || 'No subscription'
              } (${subscription?.term || 'N/A'})</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Current Price:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">$${
                subscription?.price || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Subscription Since:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                subscription?.currentPeriodStartedAt
                  ? new Date(
                      subscription.currentPeriodStartedAt
                    ).toLocaleDateString()
                  : 'N/A'
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Stripe ID:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                subscription?.providerId || 'N/A'
              }</td>
            </tr>

            <tr>
              <td colspan="2" style="padding: 24px 0 12px 0;"><strong>Usage Statistics</strong></td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Total Uploads:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                usage?.uploadCount || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>AI Interactions:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                usage?.askAICount || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Video AI Interactions:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                usage?.videoAskAICount || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Transcriptions:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                usage?.transcriptionCount || 0
              }</td>
            </tr>

            <tr>
              <td colspan="2" style="padding: 24px 0 12px 0;"><strong>Team Information</strong></td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Name:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                selectedTeam?.name || 'No team'
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Members:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                selectedTeam?.teamSpaceMembers?.length || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Created:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                selectedTeam?.createdAt
                  ? new Date(selectedTeam.createdAt).toLocaleDateString()
                  : 'N/A'
              }</td>
            </tr>

            <tr>
              <td colspan="2" style="padding: 24px 0 12px 0;"><strong>System Information</strong></td>
            </tr>
            <tr>
              <td style="padding: 12px 0;"><strong>OS:</strong></td>
              <td style="padding: 12px 0;">${window.navigator.platform}</td>
            </tr>
            <tr>
              <td style="padding: 12px 0;"><strong>Browser:</strong></td>
              <td style="padding: 12px 0;">${window.navigator.userAgent}</td>
            </tr>
          </table>
        `;

        await sendEmail({
          template: 'genericEmail',
          toEmail: 'support@screenapp.io',
          data: {
            content: wrapWithTheme(emailContent),
            subject: `Support Request from ${formData.name}`,
          },
        });

        setIsSuccess(true);
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          message: '',
        });
      } catch (error) {
        console.error('Failed to submit form:', error);
        setSnackbar({
          open: true,
          message: 'Failed to submit form. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  if (isSuccess) {
    return (
      <Container maxWidth="md">
        <StyledPaper>
          <SuccessMessage>
            <CheckCircle className="icon" />
            <Typography variant="h4" className="title">
              Thank You for Contacting Support!
            </Typography>
            <Typography variant="body1" className="subtitle">
              We have received your message and will get back to you within 1
              working day.
            </Typography>
          </SuccessMessage>
        </StyledPaper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <StyledPaper>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <StyledTextField
                required
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <StyledTextField
                required
                fullWidth
                label="Message"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                error={!!errors.message}
                helperText={errors.message}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <StyledButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  endIcon={<SendHorizontal />}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </StyledButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </StyledPaper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactSupportForm;
